/**
 * webCOA asset path resolution.
 *
 * Single source of truth for turning a (character, emote) or background
 * name into a concrete URL under the asset base. Faithful port of the
 * desktop AO2 client (2.11) `animationlayer.cpp::loadCharacterEmote`
 * (lines ~409-460) and `text_file_functions.cpp::get_image_suffix`,
 * which are authoritative. Magatama's `viewport.resolve_character_emote`
 * mirrors the same behavior and is the cross-check, but where the two
 * disagree the C++ source wins (Magatama is experimental).
 *
 * Key facts the upstream encodes that webAO got wrong:
 *   1. Every emote is tried with BOTH the leaf-prefix form ("(a)normal")
 *      AND the directory-prefix form ("(a)/normal"). webAO only ever
 *      built the leaf form, so characters that organize sprites under
 *      "(a)/" or "(b)/" directories (e.g. Kanis on this very server,
 *      "(b)/normal.gif") never resolved. We always try both.
 *   2. Animated-image extension probe order is .webp -> .apng -> .gif
 *      -> .png. Static images are .png only.
 *   3. Idle/talk emotes fall back through char "placeholder", then the
 *      theme placeholder, then a transparent pixel.
 *
 * The browser cannot stat() or enumerate a remote directory, so "does
 * this file exist" is a cached HEAD request, and case-insensitive
 * matching is approximated by also probing a lowercased variant (true
 * case-insensitivity needs server-side help, e.g. Apache mod_speling).
 */

import fileExists from "./fileExists";
import transparentPng from "../constants/transparentPng";

/** Animated-image probe order, matching get_image_suffix(static=false). */
export const ANIMATED_IMAGE_EXTS = [".webp", ".apng", ".gif", ".png"];
/** Static-image probe order, matching get_image_suffix(static=true). */
export const STATIC_IMAGE_EXTS = [".png", ".webp"];

/** Idle (a) vs talk (b) prefix preference, each in leaf + directory form. */
function emotePrefixes(talking: boolean): string[] {
  // Bare "" is the final no-prefix fallback. Order mirrors upstream:
  // preferred animation type first, then the other, then bare.
  return talking
    ? ["(b)", "(b)/", "(a)", "(a)/", ""]
    : ["(a)", "(a)/", "(b)", "(b)/", ""];
}

/** Strips a leading slash some servers prepend to subfolder emote names. */
function normalizeEmoteName(emote: string): string {
  return emote.replace(/^\/+/, "");
}

/**
 * Joins asset-base + path segments without producing double slashes or
 * dropping the base. assetBase is expected to end in "/".
 */
function joinAsset(assetBase: string, ...parts: string[]): string {
  const tail = parts
    .map((p) => p.replace(/^\/+|\/+$/g, ""))
    .filter((p) => p.length > 0)
    .join("/");
  return assetBase + tail;
}

/**
 * URL-encodes each path segment but preserves "/" separators, so a
 * subfolder emote ("HD/DGS2/normal") and prefix directories ("(a)/")
 * survive while spaces and parentheses in names get encoded.
 */
function encodeSegments(path: string): string {
  return path
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
}

export interface EmoteCandidateOptions {
  talking: boolean;
  /** Probe a lowercased name variant after the as-sent pass. */
  caseFallback?: boolean;
  /** Override extension list (defaults to animated order). */
  extensions?: string[];
}

/**
 * Builds the ordered list of candidate URLs for a character emote, with
 * no placeholder fallback (callers append those). Pure and synchronous
 * so the ordering can be unit-tested without any network.
 *
 * Order, per case-pass: for each prefix (in talk/idle preference order),
 * for each extension, "<base>characters/<char>/<prefix><emote><ext>".
 * If caseFallback is on, the whole thing is repeated with char + emote
 * lowercased (deduped against the first pass).
 */
export function buildEmoteCandidates(
  assetBase: string,
  charName: string,
  emote: string,
  opts: EmoteCandidateOptions,
): string[] {
  const exts = opts.extensions ?? ANIMATED_IMAGE_EXTS;
  const prefixes = emotePrefixes(opts.talking);
  const cleanEmote = normalizeEmoteName(emote);

  const passNames: Array<{ char: string; emote: string }> = [
    { char: charName, emote: cleanEmote },
  ];
  if (opts.caseFallback) {
    // Best-effort case fallback. We lowercase ONLY the leaf filename of
    // the emote -- the segment whose casing varies by char.ini author
    // ("Normal" vs "normal"). Character-folder and intermediate
    // subfolder names are left untouched, because on this server those
    // legitimately carry capitals (e.g. "HD/DGS2/"). True case-
    // insensitive matching across folders needs server-side help
    // (Apache mod_speling); this only papers over the common leaf case.
    const slash = cleanEmote.lastIndexOf("/");
    const head = slash >= 0 ? cleanEmote.slice(0, slash + 1) : "";
    const leaf = slash >= 0 ? cleanEmote.slice(slash + 1) : cleanEmote;
    const leafLc = leaf.toLowerCase();
    if (leafLc !== leaf) {
      passNames.push({ char: charName, emote: head + leafLc });
    }
  }

  const out: string[] = [];
  const seen = new Set<string>();
  for (const { char, emote: em } of passNames) {
    for (const prefix of prefixes) {
      for (const ext of exts) {
        const rel = encodeSegments(`characters/${char}/${prefix}${em}${ext}`);
        const url = joinAsset(assetBase, rel);
        if (!seen.has(url)) {
          seen.add(url);
          out.push(url);
        }
      }
    }
  }
  return out;
}

/**
 * Candidate URLs for a bare-named asset under a character (e.g. a
 * preanim, which carries no (a)/(b) prefix): "<char>/<name><ext>".
 * Includes the same leaf-only case fallback as emotes.
 */
export function buildBareCandidates(
  assetBase: string,
  charName: string,
  name: string,
  opts: { caseFallback?: boolean; extensions?: string[] } = {},
): string[] {
  const exts = opts.extensions ?? ANIMATED_IMAGE_EXTS;
  const clean = normalizeEmoteName(name);
  const names = [clean];
  if (opts.caseFallback) {
    const slash = clean.lastIndexOf("/");
    const head = slash >= 0 ? clean.slice(0, slash + 1) : "";
    const leaf = (slash >= 0 ? clean.slice(slash + 1) : clean).toLowerCase();
    const lc = head + leaf;
    if (lc !== clean) names.push(lc);
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const n of names) {
    for (const ext of exts) {
      const url = joinAsset(assetBase, encodeSegments(`characters/${charName}/${n}${ext}`));
      if (!seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    }
  }
  return out;
}

/** Candidate URLs for the char-local "placeholder" sprite. */
export function buildCharPlaceholderCandidates(
  assetBase: string,
  charName: string,
  extensions: string[] = ANIMATED_IMAGE_EXTS,
): string[] {
  return extensions.map((ext) =>
    joinAsset(assetBase, encodeSegments(`characters/${charName}/placeholder${ext}`)),
  );
}

export interface ResolveEmoteOptions extends EmoteCandidateOptions {
  /**
   * Ordered theme-placeholder URLs (asset-base default-theme placeholder
   * first, bundled app placeholder last), used after the char-local
   * placeholder and before the transparent-pixel ultimate fallback.
   */
  themePlaceholderUrls?: string[];
  /**
   * Existence probe. Defaults to the cached HEAD-based fileExists; the
   * test suite injects a fake, and the live client may inject a probe
   * backed by a parallel/cached strategy.
   */
  exists?: (url: string) => Promise<boolean>;
}

/**
 * Resolves a character emote to a concrete URL by probing candidates in
 * order and returning the first that exists (HEAD, cached). Falls back
 * through char placeholder -> theme placeholder -> transparent pixel,
 * exactly like upstream's placeholder_fallback chain. Sequential
 * first-hit (not parallel) to match desktop semantics and avoid firing
 * dozens of HEADs per emote at the server; misses are cached.
 */
export async function resolveCharacterEmote(
  assetBase: string,
  charName: string,
  emote: string,
  opts: ResolveEmoteOptions,
): Promise<string> {
  if (!charName || !emote) {
    return transparentPng;
  }

  const exists = opts.exists ?? fileExists;
  const primary = buildEmoteCandidates(assetBase, charName, emote, opts);
  for (const url of primary) {
    if (await exists(url)) return url;
  }

  // placeholder_fallback chain (idle/talk only in upstream, but harmless
  // to always try -- a missing emote with no placeholder still ends at
  // the transparent pixel).
  const charPlaceholders = buildCharPlaceholderCandidates(
    assetBase,
    charName,
    opts.extensions ?? ANIMATED_IMAGE_EXTS,
  );
  for (const url of charPlaceholders) {
    if (await exists(url)) return url;
  }

  for (const url of opts.themePlaceholderUrls ?? []) {
    if (await exists(url)) return url;
  }
  return transparentPng;
}

/** Convenience: ordered candidate list including placeholders, for the
 * preloader (which probes its own way). */
export function buildEmoteCandidatesWithFallback(
  assetBase: string,
  charName: string,
  emote: string,
  opts: ResolveEmoteOptions,
): string[] {
  const list = buildEmoteCandidates(assetBase, charName, emote, opts);
  list.push(
    ...buildCharPlaceholderCandidates(
      assetBase,
      charName,
      opts.extensions ?? ANIMATED_IMAGE_EXTS,
    ),
  );
  if (opts.themePlaceholderUrls) list.push(...opts.themePlaceholderUrls);
  return list;
}
